import ConfigurationService from './ConfigurationService';
import logger from './loggerService';

let initialized = false;

/**
 * Known placeholder public keys that must never reach the Sentry ingest API.
 * Checked only against the extracted `publicKey` DSN component.
 */
const PLACEHOLDER_PUBLIC_KEYS: readonly string[] = ['placeholder', 'your-key-here'] as const;

/**
 * Known placeholder project IDs that indicate a non-production DSN.
 * Checked only against the extracted numeric `projectId` DSN component.
 */
const PLACEHOLDER_PROJECT_IDS: ReadonlySet<string> = new Set(['12345']);

/**
 * Enterprise-grade Sentry DSN validator with component-level analysis.
 *
 * Parses the DSN into its constituent parts via named capture groups so that
 * placeholder detection is scoped to the `publicKey` and `projectId` segments
 * exclusively — never applied as a raw substring match on the full URL
 * (which would produce false positives on legitimate Sentry ingest hostnames
 * such as `o12345.ingest.us.sentry.io`).
 *
 * Accepted format:
 *   https://<publicKey>@<host>[:<port>]/[<path>/]<projectId>[/][?<query>]
 *
 * @param dsn - Sentry DSN string to validate
 * @returns `true` only when the DSN is structurally valid and free of placeholder values
 */
export function isValidSentryDsn(dsn: string | null | undefined): boolean {
    if (!dsn || typeof dsn !== 'string') return false;

    const trimmed = dsn.trim();
    if (!trimmed) return false;

    // Named-capture-group regex: extract publicKey and projectId as distinct segments.
    // The host segment is intentionally not inspected for placeholder strings because
    // real Sentry org-slug ingest URLs (e.g. o12345.ingest.us.sentry.io) embed
    // numeric-looking substrings that would otherwise trigger false rejections.
    const DSN_REGEX =
        /^https:\/\/(?<publicKey>[a-zA-Z0-9_-]+)@(?<host>[a-zA-Z0-9.-]+(?::[0-9]+)?)\/(?:.+\/)?(?<projectId>\d+)\/?(?:\?.*)?$/;

    const match = DSN_REGEX.exec(trimmed);
    if (!match?.groups) return false;

    const { publicKey, projectId } = match.groups;

    // Guard: reject known placeholder public keys (e.g. 'placeholder', 'your-key-here')
    if (PLACEHOLDER_PUBLIC_KEYS.some(p => publicKey.toLowerCase().includes(p))) {
        return false;
    }

    // Guard: reject known placeholder project IDs (e.g. '12345')
    if (PLACEHOLDER_PROJECT_IDS.has(projectId)) {
        return false;
    }

    return true;
}

/**
 * Lazily initializes Sentry Error Tracking.
 * Uses dynamic imports to avoid loading Sentry code during initial bundle load.
 * 
 * PERFORMANCE:
 * - Dynamic import reduces initial bundle size
 * - Checks for DSN before importing to avoid unnecessary network/parse cost
 * - Configures integrations (tracing, replay) only when enabled
 */
export async function initializeSentry(): Promise<void> {
    if (initialized) return;

    try {
        const dsn = ConfigurationService.getSentryDsn();

        // Strict guard: if no valid DSN, abort Sentry initialization entirely
        // This stops Sentry from loading in development or staging where it isn't configured,
        // saving bandwidth and preventing 400 Bad Request envelope transmission errors.
        if (!isValidSentryDsn(dsn)) {
            logger.debug('Sentry disabled (missing or invalid DSN), skipping initialization');
            return;
        }

        // Additional configuration check
        if (!ConfigurationService.isSentryEnabled()) {
            logger.debug('Sentry explicitly disabled by configuration, skipping initialization');
            return;
        }

        // Dynamic import - heavy payload loaded only when Sentry is fully active and validated
        const Sentry = await import('@sentry/react');

        Sentry.init({
            dsn: dsn,
            enabled: true, // Safe to set to true since we validated the DSN and checked config
            environment: import.meta.env.MODE,
            integrations: [
                Sentry.browserTracingIntegration(),
                // Replay is heavy, consider lazy loading or conditional sampling
                Sentry.replayIntegration()
            ],
            // Performance Monitoring
            tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod

            // Dynamic trace propagation targets
            tracePropagationTargets: [
                new RegExp(ConfigurationService.getApiBaseUrl().replace(/https?:\/\//, '')),
                'localhost',
            ],

            // Session Replay
            replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
            replaysOnErrorSampleRate: 1.0,

            beforeSend(event) {
                // Safety gate: reject sending telemetry if DSN is invalid
                if (!isValidSentryDsn(ConfigurationService.getSentryDsn())) {
                    return null;
                }
                return event;
            },
        });

        initialized = true;
        logger.info('Sentry initialized (deferred)');
    } catch (error) {
        // Don't crash the app if analytics/observability initialization fails
        logger.error('Failed to initialize Sentry', error);
    }
}

